import React, { useState, useEffect } from "react";
import { Table, Space } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { color } from "theme/Color";
import { DotIcon } from "../../../assets/icons/DotIcon";
import AButton from "./Button";
import { useAppSelector } from "../store/hooks";

const { Column } = Table;

interface ReusableTableProps {
  dataSource: any[];
  columns: any[];
  tableTitle?: React.ReactNode;
  scroll?: any;
  totalData: number;
  handleChange: (page: number) => void;
  loading?: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  rowSelection?: any;
}

const PaginationTable: React.FC<ReusableTableProps> = ({
  tableTitle,
  dataSource,
  columns,
  scroll,
  totalData,
  handleChange,
  loading,
  currentPage,
  setCurrentPage,
  rowSelection,
}) => {
  const pageSize = 10;

  // Debug: Log when currentPage prop changes
  // useEffect(() => {
  //   console.log(" PaginationTable - currentPage prop changed:", currentPage);
  //   console.log(" PaginationTable - dataSource length:", dataSource?.length);
  //   console.log(" PaginationTable - totalData:", totalData);
  // }, [currentPage, dataSource, totalData]);

  const handlePageChange = (newPage: number) => {
    // console.log(" PaginationTable - handlePageChange called with page:", newPage);
    setCurrentPage(newPage);
    handleChange(newPage);
  };

  const renderInputBox = (text: string) => {
    return <span style={{ fontSize: "14px", fontWeight: "400" }}>{text}</span>;
  };

  const renderStatusColumn = (text: string) => {
    let textColor: any = "";
    let bgColor: any = "";
    if (text === "Active" || "New") {
      textColor = color.SUCCESS;
      bgColor = color.LIGHT_SUCCESS;
    } else if (text === "Verification Pending") {
      textColor = color.WARNING;
      bgColor = color.LIGHT_WARNING;
    } else {
      textColor = color.DANGER;
      bgColor = color.LIGHT_DANGER;
    }

    return (
      <span
        style={{
          color: textColor,
          backgroundColor: bgColor,
          padding: "4px 8px",
          borderRadius: "10px",
          display: "inline-flex",
          alignItems: "center",
          fontWeight: "500",
          fontSize: "12px",
        }}
      >
        <DotIcon style={{ marginRight: "6px" }} />
        {text}
      </span>
    );
  };

  const CustomFooter = () => {
    const totalItems = totalData;
    const totalPages = Math.ceil(totalItems / pageSize);
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <AButton
            title="Previous"
            style={{ marginRight: "8px" }}
            disabled={currentPage === 1 || totalPages < 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </AButton>
          <AButton
            title="Next"
            style={{ marginRight: "8px" }}
            disabled={currentPage === totalPages || totalPages < 1}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </AButton>
        </div>
      </div>
    );
  };

  const renderTableCell = (column: any, text: any, record: any, index: any) => {
    if (column.dataIndex === "status") {
      return renderStatusColumn(text);
    }

    if (column.render) {
      return column.render(text, record, index);
    }

    return renderInputBox(text);
  };

  return (
    <Table
      scroll={{ x: "max-content" }}
      dataSource={dataSource}
      columns={columns.map((column) => ({
        ...column,
        title: <span style={{ fontWeight: "500" }}>{column.title}</span>,
        render: (text, record, index) =>
          renderTableCell(column, text, record, index),
      }))}
      title={
        tableTitle
          ? () => (
              <Space
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span style={{ fontSize: "18px", fontWeight: "500" }}>
                  {tableTitle}
                </span>
                <MoreOutlined size={24} />
              </Space>
            )
          : undefined
      }
      pagination={false}
      footer={CustomFooter}
      loading={loading}
      rowSelection={rowSelection}
    />
  );
};

export default PaginationTable;
